import { expressEventListener, expressQuerySelector, expressQuerySelectorAll } from "../common/html";
import { createPageLog, PageLogStyle } from "../components/molecules/page-log";
import { getCookie } from "./cookie";
import { IGrid } from "../components/technical/grid";
import { IUploadDialog } from "../components/popups/upload-dialog";
import { IDisposable } from "./disposable";
import { createPopup } from "../components/technical/popup-new";

export interface ISortValues {
	sortDirection: number;
	folderColumns: number;
}

export interface IPhotoGridEls {
	photoGridEl: HTMLElement;
	pageHeaderEl: HTMLElement;
	pageLogEl: HTMLElement;
	textFieldEditEl: HTMLElement;
	pageTitleEl: HTMLElement;
	folderId: string;
	uploadBatchSize: number;
}

export const getPhotoGridEls = () => {
	const photoGridEl = expressQuerySelector<HTMLElement>(document, ".technical-grid", true);
	const pageHeaderEl = expressQuerySelector<HTMLElement>(document, ".technical-header", true);
	const pageLogEl = expressQuerySelector<HTMLElement>(document, ".technical-top-message-log", true);
	const textFieldEditEl = expressQuerySelector<HTMLElement>(photoGridEl, ".technical-text-field-edit", false);
	const pageTitleEl = expressQuerySelector<HTMLElement>(document, ".technical-page-header", true);
	const folderId = photoGridEl.dataset.folderid || "";
	const uploadBatchSize = parseInt(photoGridEl.dataset.uploadbatchsize || "5", 10);

	return {
		photoGridEl,
		pageHeaderEl,
		pageLogEl,
		textFieldEditEl,
		pageTitleEl,
		folderId,
		uploadBatchSize,
	} as IPhotoGridEls;
};

export const getSortValuesForMyPhotos = (sortValue: string): ISortValues => {
	const sortDirection = sortValue.includes("Asc") ? 0 : 1;
	const folderColumns = sortValue.startsWith("DateTaken") ? 0 : sortValue.startsWith("DateUploaded") ? 1 : 3;
	return {
		sortDirection,
		folderColumns,
	};
};

export const getSortValuesForAlbums = (sortValue: string): ISortValues => {
	const sortDirection = sortValue.includes("Asc") ? 0 : 1;
	const folderColumns = sortValue.startsWith("Name") ? 3 : 0;
	return {
		sortDirection,
		folderColumns,
	};
};

export const showPageLog = (
	pageLogEl: HTMLElement,
	photoGridEl: HTMLElement,
	successfullCount: number,
	failedFileNames: string[],
): void => {
	const failedCount = failedFileNames.length;
	const totalCount = successfullCount + failedCount;
	const pageLog = createPageLog(pageLogEl);

	const accountId = getCookie("accid");
	if (accountId === "00000000-0000-0000-0000-000000000000") {
		// Logged out -> do nothing
		return;
	} else if (failedCount === 0) {
		// all success
		const logMessage = photoGridEl.dataset.success || "";
		if (!logMessage) return;

		pageLog.updateMessageAndStyle(PageLogStyle.Success, logMessage.replace("{0}", totalCount + ""));
	} else if (failedCount === totalCount) {
		// all failed
		const logMessage = photoGridEl.dataset.allfailed || "";
		if (!logMessage) return;

		pageLog.updateMessageAndStyle(PageLogStyle.Error, logMessage.replace("{0}", totalCount + ""));
	} else {
		// partial success/failed
		const logMessage = photoGridEl.dataset.somefailed || "";
		if (!logMessage) return;

		pageLog.updateMessageAndStyle(
			PageLogStyle.WarningOrange,
			logMessage
				.replace("{0}", failedCount + "")
				.replace("{1}", failedFileNames.join(", "))
				.replace("{2}", successfullCount + ""),
		);
	}

	pageLogEl.scrollTo();
};

export const removeGridItem = (gridEl: HTMLElement, itemId: string): void => {
	const allItems = expressQuerySelectorAll<HTMLElement>(gridEl, ".technical-grid-item");
	const itemToDelete = allItems.find((e) => e.dataset.itemid === itemId);
	if (itemToDelete) itemToDelete.remove();
};

export const setupGridPageCacheHandlers = (getGrid: () => IGrid | null): void => {
	window.onunload = function (_: PageTransitionEvent) {
		// keep this. We need this function to prevent firefox caching this page in the history cache.
	};
	window.onbeforeunload = function (_: PageTransitionEvent) {
		// keep this. We need this function to prevent IE11+ caching this page in the history cache.
	};
	window.onpageshow = function (event: PageTransitionEvent) {
		if (event.persisted) {
			const grid = getGrid();
			if (grid) grid.refresh(false);
		}
	};

	expressEventListener(window, "popstate", async () => {
		const grid = getGrid();
		if (grid) await grid.refresh(false);
	});
};

export const openOrderPrintsPopup = (maxAmountOfPhotos: number, submitPhotoSelection: () => void) => {
	let okButtonEventDisposable: IDisposable;
	let cancelButtonEventDisposable: IDisposable;
	return createPopup({
		dialogId: "OrderPrints",
		view: "GenericPopup",
		forceRefresh: false,
		data: {
			ExtraPopupCss: "p-default",
			TitleLabel: "Label.MyPages.OrderPrints.Dialog.Title",
			BodyLabel: "Label.MyPages.OrderPrints.Dialog.Description",
			BodyReplacements: [maxAmountOfPhotos],
			OkButtonLabel: "Label.MyPages.OrderPrints.Dialog.Continue",
			CancelButtonLabel: "Label.MyPages.OrderPrints.Dialog.Cancel",
		},
		onClose: () => {
			okButtonEventDisposable && okButtonEventDisposable.dispose();
			cancelButtonEventDisposable && cancelButtonEventDisposable.dispose();
		},
	}).then((res) => {
		const okButton = expressQuerySelector(res.popup, ".technical-general-popup-ok-button", true);
		const cancelButton = expressQuerySelector(res.popup, ".technical-general-popup-cancel-button", true);

		okButtonEventDisposable = expressEventListener(okButton, "click", (_) => submitPhotoSelection());
		cancelButtonEventDisposable = expressEventListener(cancelButton, "click", (_) => res.close());

		res.openAsync();
	});
};
